
import Vue from "vue";
import EntityIdLink from "@/components/shared/EntityIdLink/EntityIdLink.vue";
import {
  clearStripeExpressAccount,
  emailStripeExpressFixLink,
  listStripeExpressAccounts,
  recheckStripeExpressAccount,
  StripeExpressAccountRow,
  StripeExpressAccountRowResult
} from "@/api";
import {
  describeStripeExpressFreshness,
  patchStripeExpressRow,
  StripeExpressFreshness,
  StripeExpressRowPatchOutcome
} from "@/helpers/stripeExpressAccounts";

const FRESHNESS_COLORS: Record<string, string> = {
  fresh: "#13CA82",
  aging: "grey",
  stale: "#FB7672",
  unknown: "grey darken-1"
};
const FRESHNESS_TICK_MS = 60 * 1000;
const MISMATCH_MESSAGE =
  "The server returned a different Talent for this row, so it was left unchanged. Reload the page.";

export default Vue.extend({
  name: "StripeExpressAccounts",
  components: { EntityIdLink },
  data: () => ({
    loading: false,
    error: "",
    success: "",
    rows: [] as StripeExpressAccountRow[],
    actingId: null as number | null,
    acting: "" as "" | "clear" | "email" | "recheck",
    nowMs: Date.now(),
    freshnessTimer: null as ReturnType<typeof setInterval> | null,
    headers: [
      { text: "Talent", value: "fullName" },
      { text: "Hyg ID", value: "hygienistId" },
      { text: "Email", value: "email" },
      { text: "Stripe account ID", value: "stripeAccountId" },
      { text: "Country", value: "country" },
      { text: "First detected", value: "firstDetectedOn" },
      { text: "Last checked", value: "lastCheckedOn" },
      { text: "Reason it isn't working", value: "reason", sortable: false },
      { text: "Last emailed", value: "lastEmailedOn" },
      { text: "Actions", value: "actions", sortable: false, align: "end" }
    ]
  }),
  computed: {
    // Rows with no readable check time count as not recently checked too, so
    // the summary never claims a check that did not happen.
    staleCount(): number {
      return this.rows.filter(row => {
        const level = this.freshnessOf(row.lastCheckedOn).level;
        return level === "stale" || level === "unknown";
      }).length;
    },
    freshnessSummary(): string {
      const total = `${this.rows.length} ${
        this.rows.length === 1 ? "account" : "accounts"
      }`;
      if (!this.staleCount) {
        return `${total}. Every row was checked with Stripe in the last 24 hours.`;
      }
      return `${total}. ${this.staleCount} not checked with Stripe in over 24 hours; use Recheck before acting on them.`;
    }
  },
  async created() {
    this.freshnessTimer = setInterval(() => {
      this.nowMs = Date.now();
    }, FRESHNESS_TICK_MS);
    await this.loadRows();
  },
  beforeDestroy() {
    if (this.freshnessTimer) clearInterval(this.freshnessTimer);
    this.freshnessTimer = null;
  },
  methods: {
    formatStamp(value?: string | null): string {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleString();
    },
    freshnessOf(value?: string | null): StripeExpressFreshness {
      return describeStripeExpressFreshness(value, this.nowMs);
    },
    freshnessColor(value?: string | null): string {
      return FRESHNESS_COLORS[this.freshnessOf(value).level];
    },
    async loadRows() {
      this.loading = true;
      this.error = "";
      try {
        const result = await listStripeExpressAccounts();
        this.rows = Array.isArray(result?.rows) ? result.rows : [];
      } catch (err) {
        this.error =
          err && (err as Error).message
            ? (err as Error).message
            : "Failed to load Stripe Express Accounts";
        this.rows = [];
      } finally {
        this.nowMs = Date.now();
        this.loading = false;
      }
    },
    // Patch only the affected row from the action's response. Reloading the
    // whole list after every click is what FB-4 removes.
    applyRowResult(
      item: StripeExpressAccountRow,
      result: StripeExpressAccountRowResult | null | undefined
    ): StripeExpressRowPatchOutcome {
      const patched = patchStripeExpressRow(this.rows, item.id, result);
      this.rows = patched.rows;
      this.nowMs = Date.now();
      return patched.outcome;
    },
    async recheckRow(item: StripeExpressAccountRow) {
      this.actingId = item.id;
      this.acting = "recheck";
      this.error = "";
      this.success = "";
      try {
        const result = await recheckStripeExpressAccount(item.id);
        const outcome = this.applyRowResult(item, result);
        if (outcome === "mismatch") {
          this.error = MISMATCH_MESSAGE;
        } else if (outcome === "removed") {
          this.success = `Stripe no longer reports a payout problem for ${item.fullName}, so the row left this list.`;
        } else {
          this.success = `Rechecked ${item.fullName} with Stripe.`;
        }
      } catch (err) {
        this.error =
          err && (err as Error).message
            ? (err as Error).message
            : "Failed to recheck this account with Stripe";
      } finally {
        this.actingId = null;
        this.acting = "";
      }
    },
    async clearRow(item: StripeExpressAccountRow) {
      this.actingId = item.id;
      this.acting = "clear";
      this.error = "";
      this.success = "";
      try {
        const result = await clearStripeExpressAccount(item.id);
        const outcome = this.applyRowResult(item, result);
        if (outcome === "mismatch") {
          this.error = MISMATCH_MESSAGE;
        } else if (result?.cleared === false && outcome === "removed") {
          this.success = `Stripe no longer reports a payout problem for ${item.fullName}, so the row left this list.`;
        } else if (result?.cleared === false) {
          this.error = `Stripe reported a new problem for ${item.fullName} while clearing, so the row stays. Review the updated reason.`;
        } else {
          this.success = `Cleared ${item.fullName}. The row stays in history and returns if Stripe reports a new problem.`;
        }
      } catch (err) {
        this.error =
          err && (err as Error).message
            ? (err as Error).message
            : "Failed to clear this row";
      } finally {
        this.actingId = null;
        this.acting = "";
      }
    },
    async emailRow(item: StripeExpressAccountRow) {
      this.actingId = item.id;
      this.acting = "email";
      this.error = "";
      this.success = "";
      try {
        const result = await emailStripeExpressFixLink(item.id);
        this.success = `Sent the payouts fix email to ${item.email ||
          item.fullName}.`;
        if (this.applyRowResult(item, result) === "mismatch") {
          this.error = MISMATCH_MESSAGE;
        }
      } catch (err) {
        this.error =
          err && (err as Error).message
            ? (err as Error).message
            : "Failed to send the fix email";
      } finally {
        this.actingId = null;
        this.acting = "";
      }
    }
  }
});
